import { document } from '@mgmresorts/wcl-utils';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import React, { useMemo, useCallback, ReactNode } from 'react';

import { BodyScrollLockContext, BodyScrollLockContextValue } from './BodyScrollLockContext';

export const defaultAllowTouchMove = (el: HTMLElement | Element) => el.tagName === 'TEXTAREA';

/**
 * BodyScrollLockContextProvider
 * The only purpose of this provider is to enable/disable a mobile scroll lock
 * on the body HTML element when a modal is open.
 *
 * @param enable - triggers a locked state.
 * Optionally pass a specific element you wish to have touchMove capability when locked.
 *
 * @param disable - unlocks the body element.
 */

interface BodyScrollLockContextProviderProps {
  children: ReactNode;
}

export const BodyScrollLockContextProvider: React.FC<BodyScrollLockContextProviderProps> = ({ children }) => {
  const [locked, setLocked] = React.useState<boolean>(false);

  const targetElement = document?.querySelector('body');

  const enable = useCallback(
    ({ allowTouchMove }): void => {
      if (locked || !targetElement) {
        return;
      }

      disableBodyScroll(targetElement, {
        allowTouchMove,
      });

      setLocked(true);
    },
    [locked, targetElement],
  );

  const disable = useCallback(() => {
    if (!locked || !targetElement) {
      return;
    }

    enableBodyScroll(targetElement);
    setLocked(false);
  }, [locked, targetElement]);

  const ctxValue: BodyScrollLockContextValue = useMemo(
    () => ({
      enable,
      disable,
    }),
    [disable, enable],
  );

  return <BodyScrollLockContext.Provider value={ctxValue}>{children}</BodyScrollLockContext.Provider>;
};
